/**
 * EmailConnectorModal
 * SMTP/IMAP 이메일 커넥터 추가/편집 모달
 * 4탭 사이드바 구조: 기본 설정 | SMTP 발신 | IMAP 수신 | 발송 테스트
 */

import { useState, useEffect, useRef } from 'react';
import { Button, Modal, Notice, useToast } from '../ui';
import emailConnectorApi, { EmailConnectorConfig, ReceivedMail } from '../../api/email-connectors';
import SettingsIcon from '../../assets/icons/common/settings.svg?react';
import SendIcon from '../../assets/icons/common/send.svg?react';
import MailIcon from '../../assets/icons/dashboard/mail.svg?react';
import PlayIcon from '../../assets/icons/common/play.svg?react';
import InfoIcon from '../../assets/icons/common/info.svg?react';
import './EmailConnectorModal.css';

interface Props {
  connectorId?: string;
  isOpen: boolean;
  onSaved?: (config: EmailConnectorConfig) => void;
  onClose: () => void;
}

const TABS = ['기본 설정', 'SMTP 발신', 'IMAP 수신', '발송/수신 테스트'] as const;
type Tab = (typeof TABS)[number];

import type { FC, SVGProps } from 'react';

const TAB_ICONS: Record<Tab, FC<SVGProps<SVGSVGElement>>> = {
  '기본 설정': SettingsIcon,
  'SMTP 발신': SendIcon,
  'IMAP 수신': MailIcon,
  '발송/수신 테스트': PlayIcon,
};

const DEFAULT: EmailConnectorConfig = {
  name: '',
  description: '',
  isActive: true,
  isDefault: false,
  smtpHost: '',
  smtpPort: 587,
  smtpSsl: false,
  smtpAccount: '',
  smtpPassword: '',
  senderName: '',
  senderEmail: '',
  imapEnabled: false,
  imapHost: '',
  imapPort: 993,
  imapSsl: true,
  imapAccount: '',
  imapPassword: '',
  extraLabel: '',
};

const MAIL_PRESETS: Record<string, Partial<EmailConnectorConfig>> = {
  gmail: { smtpHost: 'smtp.gmail.com', smtpPort: 587, smtpSsl: false, imapHost: 'imap.gmail.com', imapPort: 993, imapSsl: true },
  naver: { smtpHost: 'smtp.naver.com', smtpPort: 587, smtpSsl: false, imapHost: 'imap.naver.com', imapPort: 993, imapSsl: true },
};

const Toggle = ({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: React.ReactNode; disabled?: boolean }) => (
  <label className={`ec-toggle${disabled ? ' ec-toggle--disabled' : ''}`}>
    <span className="ec-toggle-label">{label}</span>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
    <span className="ec-toggle-track" />
  </label>
);

const ToggleCard = ({ checked, onChange, icon, title, desc }: { checked: boolean; onChange: (v: boolean) => void; icon: React.ReactNode; title: React.ReactNode; desc?: React.ReactNode }) => (
  <label className={`ec-toggle-card${checked ? ' active' : ''}`}>
    <span className="ec-toggle-card-icon">{icon}</span>
    <span className="ec-toggle-card-text">
      <strong>{title}</strong>
      {desc && <span>{desc}</span>}
    </span>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span className="ec-toggle-track" />
  </label>
);

/** 메일 서비스별 연결 안내 (세그먼트 탭 + 제목·뱃지 + 번호 단계 + 하단 참고) */
type GuideType = 'gmail' | 'naver';
interface GuideContent {
  title: string;
  badge?: string;
  intro: React.ReactNode;
  steps: React.ReactNode[];
  note?: React.ReactNode;
}
const GuideTabs = ({ value, onChange, contents }: {
  value: GuideType;
  onChange: (v: GuideType) => void;
  contents: Record<GuideType, GuideContent>;
}) => {
  const c = contents[value];
  return (
    <div className="ec-guide">
      {/* 헤더 - 왼쪽 제목 / 오른쪽 알약 탭 */}
      <div className="ec-guide-header">
        <strong className="ec-guide-header-title">메일 서비스별 연결 가이드</strong>
        <div className="ec-guide-tabs" role="tablist">
          {(['gmail', 'naver'] as const).map((type) => (
            <button key={type} type="button" role="tab" aria-selected={value === type}
              className={`ec-guide-tab${value === type ? ' active' : ''}`}
              onClick={() => onChange(type)}>
              {type === 'gmail' ? 'Gmail' : 'Naver'}
            </button>
          ))}
        </div>
      </div>
      <div className="ec-guide-content">
        <div className="ec-guide-head">
          <strong className="ec-guide-title">{c.title}</strong>
        </div>
        <p className="ec-guide-intro">{c.intro}</p>
        <ol className="ec-guide-steps">
          {c.steps.map((step, i) => (
            <li key={i}>
              <span className="ec-guide-step-num">{i + 1}</span>
              <span className="ec-guide-step-text">{step}</span>
            </li>
          ))}
        </ol>
        {c.note && (
          <li className="ec-guide-note">
            <InfoIcon />
            <span>{c.note}</span>
          </li>
        )}
      </div>
    </div>
  );
};

/** SMTP 발신 탭 안내 */
const SMTP_GUIDE: Record<GuideType, GuideContent> = {
  gmail: {
    title: 'Gmail 연결 방법',
    intro: 'Gmail은 앱 비밀번호를 발급받아 사용합니다. 일반 계정 비밀번호로는 연결되지 않습니다.',
    steps: [
      <><a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer">Google 계정 보안 설정</a>에 접속하세요.</>,
      <><strong>2단계 인증</strong>을 활성화하세요. (이미 켜져 있다면 다음 단계로)</>,
      <>보안 페이지에서 <strong>앱 비밀번호</strong>를 검색해 선택하세요.</>,
      <>앱은 <strong>메일</strong>, 기기는 <strong>Windows 컴퓨터</strong>로 선택 후 생성하세요.</>,
      <>발급된 <strong>16자리 비밀번호</strong>를 비밀번호 필드에 입력하세요.</>,
    ],
    note: '발신자 이름·이메일을 비워두면 계정 정보로 대체됩니다.',
  },
  naver: {
    title: 'Naver 연결 방법',
    intro: 'Naver 메일은 IMAP/SMTP 사용 설정과 앱 비밀번호가 필요합니다.',
    steps: [
      <><a href="https://mail.naver.com" target="_blank" rel="noreferrer">네이버 메일</a> → 환경설정 → <strong>POP3/IMAP 설정</strong>에서 IMAP/SMTP 사용 후 저장하세요.</>,
      <><a href="https://nid.naver.com/security" target="_blank" rel="noreferrer">네이버 계정 보안</a>에서 <strong>2단계 인증</strong>을 활성화하세요.</>,
      <><strong>앱 비밀번호</strong>를 발급받으세요.</>,
      <>발급된 비밀번호를 비밀번호 필드에 입력하세요.</>,
    ],
    note: '발신자 이름·이메일을 비워두면 계정 정보로 대체됩니다.',
  },
};

/** IMAP 수신 탭 안내 */
const IMAP_GUIDE: Record<GuideType, GuideContent> = {
  gmail: {
    title: 'Gmail IMAP 설정',
    badge: 'IMAP 사용 설정',
    intro: 'Gmail에서 IMAP을 켜야 메일을 가져올 수 있습니다.',
    steps: [
      <><a href="https://mail.google.com/mail/u/0/#settings/fwdandpop" target="_blank" rel="noreferrer">Gmail 설정 → 전달 및 POP/IMAP</a>에서 <strong>IMAP 사용</strong>을 활성화하세요.</>,
      <>IMAP 계정·비밀번호는 SMTP와 동일하게 입력하세요. (앱 비밀번호 공유)</>,
    ],
    note: 'INBOX는 항상 포함됩니다. 추가 라벨을 입력하면 해당 폴더도 함께 가져옵니다.',
  },
  naver: {
    title: 'Naver IMAP 설정',
    badge: 'IMAP 사용 설정',
    intro: 'SMTP 설정에서 IMAP/SMTP 사용을 이미 활성화했다면 별도 설정 없이 사용 가능합니다.',
    steps: [
      <><a href="https://mail.naver.com" target="_blank" rel="noreferrer">네이버 메일</a> → 환경설정 → <strong>POP3/IMAP 설정</strong>에서 IMAP/SMTP 사용을 확인하세요.</>,
      <>IMAP 계정·비밀번호는 SMTP와 동일하게 입력하세요. (앱 비밀번호 공유)</>,
    ],
    note: 'INBOX는 항상 포함됩니다. 추가 라벨을 입력하면 해당 폴더도 함께 가져옵니다.',
  },
};

const Field = ({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) => (
  <div className="form-item">
    <label className="form-label">
      {label}
      {required && <span className="required">*</span>}
      {hint && <p className="optional">{hint}</p>}
    </label>
    {children}
  </div>
);

const PortInput = ({ value, onChange, disabled }: { value: number; onChange: (v: number) => void; disabled?: boolean }) => (
  <div className="ec-port-input">
    <button type="button" onClick={() => onChange(Math.max(1, value - 1))} className="ec-port-btn" disabled={disabled}>−</button>
    <div className={`input ec-port-field${disabled ? ' input--disabled' : ''}`}>
      <input className="input-control" type="number" value={value} onChange={(e) => onChange(parseInt(e.target.value) || value)} disabled={disabled} />
    </div>
    <button type="button" onClick={() => onChange(value + 1)} className="ec-port-btn" disabled={disabled}>+</button>
  </div>
);

export function EmailConnectorModal({ connectorId, isOpen, onSaved, onClose }: Props) {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>('기본 설정');
  const [form, setForm] = useState<EmailConnectorConfig>({ ...DEFAULT });
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [inbox, setInbox] = useState<ReceivedMail[]>([]);
  // 메일 종류 드롭다운 (선택 시 SMTP/IMAP 자동 채움)
  const [mailType, setMailType] = useState<'gmail' | 'naver' | ''>('');
  // 가이드 탭 (항상 표시, 드롭다운과 독립)
  const [guideTab, setGuideTab] = useState<'gmail' | 'naver'>('gmail');

  const dialogRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, initX: 0, initY: 0 });
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const handleHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = dialogRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, initX: rect.left, initY: rect.top };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current.dragging) return;
      setPos({ x: dragRef.current.initX + ev.clientX - dragRef.current.startX, y: dragRef.current.initY + ev.clientY - dragRef.current.startY });
    };
    const onUp = () => { dragRef.current.dragging = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('기본 설정');
    setPos(null);
    setInbox([]);
    setTestEmail('');
    setShowErrors(false);
    setMailType('');
    if (connectorId) {
		emailConnectorApi.get(connectorId).then((data) => {
		setForm(data);
		setMailType((data.mailType as 'gmail' | 'naver' | '') || '');
		}).catch(() => showToast('error', '커넥터 정보를 불러오지 못했습니다.'));
    } else {
      setForm({ ...DEFAULT });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, connectorId]);

  const update = (partial: Partial<EmailConnectorConfig>) => setForm((prev) => ({ ...prev, ...partial }));

  const handleMailTypeChange = (type: 'gmail' | 'naver' | '') => {
    setMailType(type);
    if (type && MAIL_PRESETS[type]) update(MAIL_PRESETS[type]);
  };

  const tabHasError = (tab: Tab): boolean => {
    if (!showErrors) return false;
    switch (tab) {
      case '기본 설정': return !form.name.trim() || !mailType;
      case 'SMTP 발신': return !form.smtpAccount.trim();
      case 'IMAP 수신': return !!form.imapEnabled && (!form.imapHost?.trim() || !form.imapAccount?.trim());
      default: return false;
    }
  };

  const handleSave = async () => {
    setShowErrors(true);
    if (!form.name.trim()) { showToast('error', '커넥터 이름을 입력해주세요.'); setActiveTab('기본 설정'); return; }
    if (!mailType) { showToast('error', '메일 종류를 선택해주세요.'); setActiveTab('기본 설정'); return; }
    if (!form.smtpAccount.trim()) { showToast('error', 'SMTP 계정(이메일)을 입력해주세요.'); setActiveTab('SMTP 발신'); return; }
    if (form.imapEnabled && (!form.imapHost?.trim() || !form.imapAccount?.trim())) { showToast('error', 'IMAP 호스트와 계정을 입력해주세요.'); setActiveTab('IMAP 수신'); return; }
    setSaving(true);
    try {
      const saved = await emailConnectorApi.save(form);
      showToast('success', '이메일 커넥터가 저장되었습니다.');
      onSaved?.(saved);
      onClose();
    } catch (err: unknown) {
      showToast('error', (err as Error).message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSmtpTest = async () => {
    if (!form.smtpHost || !form.smtpAccount) { showToast('error', 'SMTP 호스트와 계정을 입력하세요.'); return; }
    try {
      await emailConnectorApi.testSmtp(form.connectorId || 'new', form);
      showToast('success', 'SMTP 연결 성공!');
    } catch (err: unknown) {
      showToast('error', (err as Error).message || 'SMTP 연결 실패');
    }
  };

  const handleSendTest = async () => {
    if (!form.connectorId) { showToast('info', '먼저 저장 후 테스트하세요.'); return; }
    if (!testEmail.trim()) { showToast('error', '수신자 이메일을 입력하세요.'); return; }
    setTesting(true);
    try {
      await emailConnectorApi.sendTest(form.connectorId, testEmail);
      showToast('success', '테스트 메일을 발송했습니다.');
    } catch (err: unknown) {
      showToast('error', (err as Error).message || '발송 실패');
    } finally {
      setTesting(false);
    }
  };

  const handleCheckInbox = async () => {
    if (!form.connectorId) { showToast('info', '먼저 저장 후 확인하세요.'); return; }
    setChecking(true);
    try {
      const mails = await emailConnectorApi.checkInbox(form.connectorId);
      setInbox(mails);
      if (mails.length === 0) showToast('info', '수신된 메일이 없습니다.');
    } catch (err: unknown) {
      showToast('error', (err as Error).message || '수신 확인 실패');
    } finally {
      setChecking(false);
    }
  };

  const modalStyle = pos
    ? { position: 'fixed' as const, left: pos.x, top: pos.y, margin: 0, width: 700, maxWidth: '90vw' }
    : { width: 700, maxWidth: '90vw' };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={connectorId ? '이메일 커넥터 수정' : '이메일 커넥터 추가'}
      size="lg"
      className='ec-modal'
      style={modalStyle}
      dialogRef={dialogRef}
      headerProps={{ onMouseDown: handleHeaderMouseDown, style: { cursor: 'move' } }}
      footerRight={
        <>
          <Button color="gray" appearance="outline" onClick={onClose}>닫기</Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</Button>
        </>
      }
    >
      <div className="ec-body">

        {/* ── 사이드바 탭 ── */}
        <div className="ec-sidebar">
          {TABS.map((tab) => {
            const TabIcon = TAB_ICONS[tab];
            return (
              <button key={tab} type="button" onClick={() => setActiveTab(tab)}
                className={`ec-sidebar-btn${activeTab === tab ? ' active' : ''}${tabHasError(tab) ? ' has-error' : ''}`}>
                <span className="ec-sidebar-icon"><TabIcon /></span>
                <span>{tab}</span>
                {tabHasError(tab) && <span className="ec-tab-badge" />}
              </button>
            );
          })}
        </div>

        {/* ── 탭 컨텐츠 ── */}
        <div className="ec-content">

          {/* ─── 기본 설정 ─── */}
          {activeTab === '기본 설정' && (
            <div className="form-grid">
              <Field label="커넥터 이름" required>
                <div className={`input${showErrors && !form.name.trim() ? ' input--error' : ''}`}>
                  <input className="input-control" type="text" placeholder="예: 회사 공식 이메일"
                    value={form.name} onChange={(e) => update({ name: e.target.value })} />
                </div>
              </Field>

              <Field label="설명">
                <textarea placeholder="이 커넥터에 대한 설명 (선택)" value={form.description || ''}
                  onChange={(e) => update({ description: e.target.value })}
                  rows={3} style={{ resize: 'vertical', minHeight: '72px' }} />
              </Field>

              {/* 메일 종류 드롭다운 (선택 시 SMTP/IMAP 자동 채움) */}
              <Field label="메일 종류" hint="선택하면 SMTP/IMAP 호스트·포트가 자동으로 입력됩니다">
                <div className={`input${showErrors && !mailType ? ' input--error' : ''}`}>
                  <select value={mailType}
                    onChange={(e) => handleMailTypeChange(e.target.value as 'gmail' | 'naver' | '')}>
                    <option value="">선택하세요</option>
                    <option value="gmail">Gmail</option>
                    <option value="naver">Naver</option>
                  </select>
                </div>
              </Field>
            </div>
          )}

          {/* ─── SMTP 발신 ─── */}
          {activeTab === 'SMTP 발신' && (
            <>
            <div className="form-grid form-grid--2">
              <Field label="SMTP 호스트" required>
                <div className={`input${showErrors && !form.smtpHost.trim() ? ' input--error' : ''}`}>
                  <input className="input-control" type="text" placeholder="smtp.gmail.com"
                    value={form.smtpHost} onChange={(e) => update({ smtpHost: e.target.value })}
                    disabled={!!mailType} />
                </div>
              </Field>
              <Field label="포트" hint="587=STARTTLS, 465=SSL, 25=일반">
                <PortInput value={form.smtpPort || 587} onChange={(v) => update({ smtpPort: v })}
                  disabled={!!mailType} />
              </Field>
            </div>
            {/* 보안 연결 - 포트 587(STARTTLS)은 자동 처리되므로 숨김
            <div className="form-grid">
              <div className='form-item'>
                <div className='form-label'>보안 연결</div>
                <Toggle checked={form.smtpSsl || false} onChange={(v) => update({ smtpSsl: v })}
                  label={`SSL/TLS 사용 (포트 ${form.smtpPort || 587})`}
                  disabled={!!mailType} />
              </div>
            </div>
            */}
            <div className='form-grid'>
              <Field label="계정 (이메일)" required>
                <div className={`input${showErrors && !form.smtpAccount.trim() ? ' input--error' : ''}`}>
                  <input className="input-control" type="text" placeholder="your@email.com"
                    value={form.smtpAccount} onChange={(e) => update({ smtpAccount: e.target.value })} />
                </div>
              </Field>
              <Field label="비밀번호" hint="비워두면 기존 비밀번호 유지">
                <div className="input">
                  <input className="input-control" type="password" placeholder="비밀번호 또는 앱 비밀번호"
                    value={form.smtpPassword || ''} onChange={(e) => update({ smtpPassword: e.target.value })} />
                </div>
              </Field>
              <Field label="발신자 이름">
                <div className="input">
                  <input className="input-control" type="text" placeholder="홍길동"
                    value={form.senderName || ''} onChange={(e) => update({ senderName: e.target.value })} />
                </div>
              </Field>
              <Field label="발신자 이메일">
                <div className="input">
                  <input className="input-control" type="text" placeholder="sender@example.com"
                    value={form.senderEmail || ''} onChange={(e) => update({ senderEmail: e.target.value })} />
                </div>
              </Field>
            </div>
            <div className="form-grid">
              <div className='ec-smtp-test-row'>
                <Button color="primary" appearance="fill" onClick={handleSmtpTest}>SMTP 연결 테스트</Button>
                {!form.connectorId && <span className="ec-hint">저장 후 테스트 가능</span>}
              </div>
            </div>
            <div className="form-grid">
              <GuideTabs value={guideTab} onChange={setGuideTab} contents={SMTP_GUIDE} />
            </div>
            </>
          )}

          {/* ─── IMAP 수신 ─── */}
          {activeTab === 'IMAP 수신' && (
            <div className="ec-section-col">
              <ToggleCard checked={form.imapEnabled || false} onChange={(v) => update({ imapEnabled: v })}
                icon={<MailIcon />} title="IMAP 수신" desc="메일함을 주기적으로 확인해 새 메일을 가져옵니다" />
              {form.imapEnabled && (
                <div className="form-grid form-grid--2">
                  <Field label="IMAP 호스트" required>
                    <div className="input">
                      <input className="input-control" type="text" placeholder="imap.gmail.com"
                        value={form.imapHost || ''} onChange={(e) => update({ imapHost: e.target.value })}
                        disabled={!!mailType} />
                    </div>
                  </Field>
                  <Field label="포트" hint="993=SSL, 143=일반">
                    <PortInput value={form.imapPort || 993} onChange={(v) => update({ imapPort: v })}
                      disabled={!!mailType} />
                  </Field>
                  <div className="ec-col-full">
                    <Field label="보안연결">
                      <Toggle checked={form.imapSsl ?? true} onChange={(v) => update({ imapSsl: v })}
                        label={`SSL/TLS 사용 (포트 ${form.imapPort || 993})`}
                        disabled={!!mailType} />
                    </Field>
                  </div>
                  <Field label="IMAP 계정" hint="미입력 시 SMTP 계정 사용">
                    <div className="input">
                      <input className="input-control" type="text" placeholder="your@email.com"
                        value={form.imapAccount || ''} onChange={(e) => update({ imapAccount: e.target.value })} />
                    </div>
                  </Field>
                  <Field label="IMAP 비밀번호" hint="비워두면 기존 비밀번호 유지">
                    <div className="input">
                      <input className="input-control" type="password" placeholder="SMTP 비밀번호와 동일하면 비워두세요"
                        value={form.imapPassword || ''} onChange={(e) => update({ imapPassword: e.target.value })} />
                    </div>
                  </Field>
                  <div className="ec-col-full">
                    <Field label="추가 수신 라벨" hint="INBOX는 항상 폴링됩니다. 추가로 폴링할 폴더만 입력">
                      <div className="input">
                        <input className="input-control" type="text" placeholder="AcXion-On (비워두면 INBOX만 사용)"
                          value={form.extraLabel || ''} onChange={(e) => update({ extraLabel: e.target.value })} />
                      </div>
                    </Field>
                  </div>
                </div>
              )}
              <GuideTabs value={guideTab} onChange={setGuideTab} contents={IMAP_GUIDE} />
            </div>
          )}

          {/* ─── 발송/수신 테스트 ─── */}
          {activeTab === '발송/수신 테스트' && (
            <div className="ec-section-col--gap20">
              {!form.connectorId && <Notice color="warning" title='테스트를 실행하려면 먼저 커넥터를 저장하세요.' />}
              <div className="ec-test-section">
                <div className="ec-test-section-title">발송 테스트</div>
                <Field label="수신자 이메일">
                  <div className="input ec-test-email">
                    <input className="input-control" type="email" placeholder="test@example.com"
                      value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
                    <Button color="primary" onClick={handleSendTest} disabled={testing || checking || !form.connectorId}>
                      {testing ? '발송 중...' : '테스트 메일 발송'}
                    </Button>
                  </div>
                </Field>
              </div>
              <div className="ec-test-section">
                <div className="ec-test-section-title">수신 테스트</div>
                <div className="ec-btn-row">
                  <Button color="primary" onClick={handleCheckInbox} disabled={checking || testing || !form.connectorId}>
                    {checking ? '확인 중...' : '수신 메일 확인'}
                  </Button>
                </div>
                {inbox.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div className="ec-inbox-title">수신된 메일 ({inbox.length}건)</div>
                    <div className="ec-mail-list">
                      {inbox.map((mail) => (
                        <div key={mail.uid} className="ec-mail-item">
                          <div className="ec-mail-subject">{mail.subject}</div>
                          <div className="ec-mail-meta">{mail.from} · {mail.date}</div>
                          <div className="ec-mail-snippet">{mail.snippet}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </Modal>
  );
}

export default EmailConnectorModal;
